export const SUPPORTED_LANGUAGES = ["en", "ar", "ku"];

export const isSupportedLanguage = (lang) =>
  SUPPORTED_LANGUAGES.includes(String(lang || "").toLowerCase().split("-")[0]);

export const normalizeLanguage = (lang) => {
  const code = String(lang || "en").toLowerCase().split("-")[0];
  return isSupportedLanguage(code) ? code : "en";
};

export const getLanguageFromPathname = (pathname = "") => {
  const segment = String(pathname || "")
    .split("/")
    .filter(Boolean)[0];
  return isSupportedLanguage(segment) ? normalizeLanguage(segment) : null;
};

export const getPreferredLanguage = () => {
  if (typeof window !== "undefined") {
    const fromPath = getLanguageFromPathname(window.location.pathname);
    if (fromPath) return fromPath;
    try {
      const saved = localStorage.getItem("irqgyp_lang");
      if (saved) return normalizeLanguage(saved);
    } catch {
      // ignore storage errors
    }
  }
  return "en";
};

const PATHS_WITHOUT_LANG = ["/login", "/dashboard", "/404"];

export const shouldSkipLanguagePrefix = (pathname = "") => {
  const path = String(pathname || "").split("?")[0].split("#")[0] || "/";
  return PATHS_WITHOUT_LANG.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

/** Remove a leading /en|/ar|/ku segment from a pathname. */
export const stripLanguagePrefix = (pathname = "/") => {
  const raw = String(pathname || "/");
  const parts = raw.split("/");
  if (parts.length > 1 && isSupportedLanguage(parts[1])) {
    const rest = `/${parts.slice(2).join("/")}`;
    if (rest === "/") return "/";
    return rest.replace(/\/+$/, "") || "/";
  }
  return raw || "/";
};

const splitPath = (path) => {
  const value = String(path || "");
  const hashIndex = value.indexOf("#");
  const withHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const hash = hashIndex >= 0 ? value.slice(hashIndex) : "";
  const queryIndex = withHash.indexOf("?");
  const pathname = queryIndex >= 0 ? withHash.slice(0, queryIndex) : withHash;
  const search = queryIndex >= 0 ? withHash.slice(queryIndex) : "";
  return { pathname: pathname || "/", search, hash };
};

/**
 * Prefix a public path with the active language.
 * Leaves external URLs, hashes, dashboard/login/404 alone.
 */
export const localizePath = (path, lang = getPreferredLanguage()) => {
  if (path == null) return path;
  if (typeof path !== "string") return path;

  const trimmed = path.trim();
  if (!trimmed) return trimmed;
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("#")) return trimmed;

  const { pathname, search, hash } = splitPath(trimmed.startsWith("/") ? trimmed : `/${trimmed}`);
  if (shouldSkipLanguagePrefix(pathname)) {
    return `${pathname}${search}${hash}`;
  }

  const bare = stripLanguagePrefix(pathname);
  const code = normalizeLanguage(lang);
  if (bare === "/") return `/${code}${search}${hash}`;
  return `/${code}${bare}${search}${hash}`;
};

/** Swap only the language segment, keep the rest of the current public path. */
export const swapLanguageInPath = (pathname, nextLang, search = "", hash = "") => {
  const bare = stripLanguagePrefix(pathname || "/");
  return localizePath(`${bare}${search || ""}${hash || ""}`, nextLang);
};
